import { token, webApi } from '../../../utils/http';

const JSON_HEADERS = {
  Accept: 'application/json',
  'Content-Type': 'application/json; charset=utf-8',
};

const apiUrl = (path) =>
  `${webApi.replace(/\/?$/, '/')}${path.replace(/^\//, '')}`;

export class NawiChatApiError extends Error {
  constructor(
    code,
    { requestId, retryAfterSeconds, safeMessage, scope, status } = {},
  ) {
    super(`nawi_chat_error:${code}`);
    this.name = 'NawiChatApiError';
    this.code = code;
    this.requestId = requestId;
    this.retryAfterSeconds = retryAfterSeconds;
    this.safeMessage = safeMessage;
    this.scope = scope;
    this.status = status;
  }
}

const authenticatedHeaders = (headers = {}) => {
  if (!token) throw new NawiChatApiError('unauthenticated', { status: 401 });

  return {
    ...headers,
    Authorization: `Bearer ${token}`,
  };
};

const readJson = async (response) => {
  const body = await response.text();
  if (!body) return null;

  try {
    return JSON.parse(body);
  } catch (_error) {
    throw new NawiChatApiError('invalid_response', {
      status: response.status,
    });
  }
};

const toApiError = (response, body) => {
  const error = body?.error;
  const isBlocked = response.status === 422 && body?.blocked === true;

  return new NawiChatApiError(
    isBlocked ? 'moderation_blocked' : error?.code || 'request_failed',
    {
      requestId: error?.request_id,
      retryAfterSeconds: error?.retry_after_seconds,
      scope: error?.scope,
      safeMessage:
        isBlocked && typeof body.message === 'string'
          ? body.message
          : undefined,
      status: response.status,
    },
  );
};

const requestJson = async (path, { body, signal } = {}) => {
  let response;
  try {
    response = await fetch(apiUrl(path), {
      body: body === undefined ? undefined : JSON.stringify(body),
      cache: 'no-store',
      headers: authenticatedHeaders(JSON_HEADERS),
      method: 'POST',
      signal,
    });
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new NawiChatApiError('network_error');
  }

  const json = await readJson(response);
  if (!response.ok) throw toApiError(response, json);
  return json;
};

const bodyWithAccount = (body, accountId) => {
  if (!Number.isInteger(accountId) || accountId <= 0) return body;
  return { ...body, account_id: accountId };
};

const parseJsonEventData = (data, eventName) => {
  try {
    return JSON.parse(data);
  } catch (_error) {
    throw new NawiChatApiError(`malformed_${eventName}_event`);
  }
};

const parseFrame = (frame) => {
  const lines = frame.split(/\r?\n/);
  const data = [];
  let eventName = 'message';
  let hasField = false;
  let hasComment = false;

  lines.forEach((line) => {
    if (!line) return;
    if (line.startsWith(':')) {
      hasComment = true;
      return;
    }
    hasField = true;
    const separator = line.indexOf(':');
    const field = separator === -1 ? line : line.slice(0, separator);
    const rawValue = separator === -1 ? '' : line.slice(separator + 1);
    const value = rawValue.startsWith(' ') ? rawValue.slice(1) : rawValue;

    if (field === 'event') eventName = value;
    if (field === 'data') data.push(value);
  });

  if (!hasField) return hasComment ? { data: '', eventName: 'heartbeat' } : null;
  if (eventName === 'heartbeat') return { data: data.join('\n'), eventName };
  if (data.length === 0) throw new NawiChatApiError('malformed_sse_event');
  return { data: data.join('\n'), eventName };
};

const takeFrame = (buffer) => {
  const separator = /\r?\n\r?\n/.exec(buffer);
  if (!separator) return null;

  return {
    frame: buffer.slice(0, separator.index),
    rest: buffer.slice(separator.index + separator[0].length),
  };
};

const consumeFrame = (rawFrame, state, callbacks) => {
  const frame = parseFrame(rawFrame);
  if (!frame) return null;

  switch (frame.eventName) {
    case 'heartbeat':
      return null;
    case 'meta': {
      const meta = parseJsonEventData(frame.data, 'meta');
      if (!meta || typeof meta !== 'object' || typeof meta.chatId !== 'string') {
        throw new NawiChatApiError('malformed_meta_event');
      }
      if (state.chatId && state.chatId !== meta.chatId) {
        throw new NawiChatApiError('chat_identity_changed');
      }
      state.chatId = meta.chatId;
      callbacks.onMeta?.(meta);
      return null;
    }
    case 'chunk': {
      const chunk = parseJsonEventData(frame.data, 'chunk');
      if (typeof chunk !== 'string') {
        throw new NawiChatApiError('malformed_chunk_event');
      }
      state.receivedChunk = true;
      callbacks.onChunk?.(chunk);
      return null;
    }
    case 'done': {
      const done = parseJsonEventData(frame.data, 'done');
      if (!done || typeof done !== 'object' || !state.chatId) {
        throw new NawiChatApiError('malformed_done_event');
      }
      callbacks.onDone?.(done);
      return { done, chatId: state.chatId, receivedChunk: state.receivedChunk };
    }
    case 'error':
      // The data is a provider string; it is intentionally not exposed. The
      // UI shows fixed copy and keeps the chunks already delivered.
      throw new NawiChatApiError('stream_error');
    default:
      throw new NawiChatApiError('unsupported_sse_event');
  }
};

const consumeSse = async (response, callbacks = {}) => {
  if (!response.body || typeof response.body.getReader !== 'function') {
    throw new NawiChatApiError('stream_unavailable');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  const state = { chatId: null, receivedChunk: false };
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    buffer += decoder.decode(value || new Uint8Array(), { stream: !done });

    let next = takeFrame(buffer);
    while (next) {
      buffer = next.rest;
      const terminal = consumeFrame(next.frame, state, callbacks);
      if (terminal) {
        await reader.cancel?.();
        return terminal;
      }
      next = takeFrame(buffer);
    }

    if (done) break;
  }

  if (buffer.trim()) throw new NawiChatApiError('incomplete_sse_frame');
  throw new NawiChatApiError('incomplete_sse_stream');
};

export const preparePersonContext = ({ accountId, dob, signal, userPlayerId }) =>
  requestJson(
    `api/integrations/nawi/person-context/${encodeURIComponent(userPlayerId)}`,
    {
      body: bodyWithAccount(dob ? { dob } : {}, accountId),
      signal,
    },
  );

export const sendChatMessage = async ({
  accountId,
  chatId,
  clientMessageId,
  content,
  onChunk,
  onDone,
  onMeta,
  signal,
  userPlayerId,
}) => {
  let response;
  try {
    response = await fetch(
      apiUrl(
        `api/integrations/nawi/chat/${encodeURIComponent(userPlayerId)}/messages`,
      ),
      {
        body: JSON.stringify(
          bodyWithAccount(
            {
              chatId,
              clientMessageId,
              content,
            },
            accountId,
          ),
        ),
        cache: 'no-store',
        headers: authenticatedHeaders({
          Accept: 'text/event-stream',
          'Content-Type': 'application/json; charset=utf-8',
        }),
        method: 'POST',
        signal,
      },
    );
  } catch (error) {
    if (error?.name === 'AbortError') throw error;
    throw new NawiChatApiError('network_error');
  }

  if (!response.ok) {
    const json = await readJson(response);
    throw toApiError(response, json);
  }

  const contentType = response.headers.get('content-type') || '';
  if (!contentType.toLowerCase().startsWith('text/event-stream')) {
    throw new NawiChatApiError('invalid_stream_content_type', {
      status: response.status,
    });
  }

  return consumeSse(response, { onChunk, onDone, onMeta });
};

export const getRelatedTopics = async ({
  accountId,
  chatId,
  signal,
  userPlayerId,
}) => {
  const result = await requestJson(
    `api/integrations/nawi/chat/${encodeURIComponent(
      userPlayerId,
    )}/chats/${encodeURIComponent(chatId)}/topics`,
    { body: bodyWithAccount({}, accountId), signal },
  );

  if (
    !Array.isArray(result?.topics) ||
    result.topics.length > 10 ||
    result.topics.some((topic) => typeof topic !== 'string')
  ) {
    throw new NawiChatApiError('invalid_topics_response');
  }

  return result.topics;
};

// Same entitlement PersonDetailScreen uses to decide whether to render the chat.
const NAWI_ROLE = 'nawi';

// Best-effort revocation of the NAWI session on logout. It only runs for users
// with the NAWI role, never throws and never rejects, so it cannot block the
// logout. `keepalive` lets the request outlive the page if it navigates away.
export const revokeNawiSession = async (user) => {
  if (!user?.roles?.includes(NAWI_ROLE)) return;

  try {
    await fetch(apiUrl('api/integrations/nawi/session'), {
      cache: 'no-store',
      headers: authenticatedHeaders({ Accept: 'application/json' }),
      keepalive: true,
      method: 'DELETE',
    });
  } catch (_error) {
    // Logout must never depend on the NAWI revocation outcome.
  }
};
